import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, take, timeout, catchError } from 'rxjs/operators';
import { firstValueFrom, throwError } from 'rxjs';
import { registerRequiredRequests } from '../../store/app-init/app-init.actions';
import { selectIsAppInitialized } from '../../store/app-init/app-init.selectors';
import { loadDomains } from '../../store/entities/domains/domains.actions';
import { loadUser } from '../../store/entities/user/user.actions';
import { loadProfile } from '../../store/entities/profile/profile.actions';
import { Auth0Service } from '../auth/services/auth0.service';
import { isPublicRoute } from '../auth/public-routes';

@Injectable({
  providedIn: 'root',
})
export class AppInitService {
  private store = inject(Store);
  private auth0Service = inject(Auth0Service);
  private readonly INITIALIZATION_TIMEOUT = 10000;
  // Account requests skipped because the app started on a public page.
  private deferredRequests: string[] | null = null;

  async initialize(
    requiredRequests: string[],
    timeoutMs: number = this.INITIALIZATION_TIMEOUT
  ): Promise<void> {
    await this.auth0Service.init();

    // Unauthenticated after init means a login redirect is already in flight;
    // firing the data loads now would send tokenless requests that 401.
    if (!this.auth0Service.isAuthenticated()) {
      return;
    }

    // Public e-link pages and their /callback serve visitors who signed in through
    // their company but have no account here. Their /me, /profile and
    // /profile/domains answer 401, which signs them out and starts the sign-in over.
    // An account holder who moves on to an app page gets them from loadDeferredAccount.
    if (isPublicRoute()) {
      this.deferredRequests = requiredRequests;

      return;
    }

    this.loadAccount(requiredRequests);

    try {
      await firstValueFrom(
        this.store.select(selectIsAppInitialized).pipe(
          filter((isInitialized) => isInitialized),
          take(1),
          timeout(timeoutMs),
          catchError((error) => {
            if (error.name === 'TimeoutError') {
              throw new Error(
                `App initialization timed out after ${timeoutMs}ms. Required requests: ${requiredRequests.join(', ')}`
              );
            }

            return throwError(() => error);
          })
        )
      );
    } catch (error) {
      console.error('App initialization failed:', error);
      throw error;
    }
  }

  /**
   * Loads the account requests skipped at a public start, once. Called by the
   * guard of every app page that needs them; does nothing otherwise.
   */
  loadDeferredAccount(): void {
    if (!this.deferredRequests) {
      return;
    }

    const requests = this.deferredRequests;

    this.deferredRequests = null;
    this.loadAccount(requests);
  }

  private loadAccount(requiredRequests: string[]): void {
    this.store.dispatch(registerRequiredRequests({ requestKeys: requiredRequests }));

    // Dispatch load actions for required requests
    // Each entity module handles its own loading logic independently
    if (requiredRequests.includes('domains')) {
      this.store.dispatch(loadDomains());
    }

    if (requiredRequests.includes('user')) {
      this.store.dispatch(loadUser());
    }

    if (requiredRequests.includes('profile')) {
      this.store.dispatch(loadProfile());
    }
  }
}

